import { getMetadataRegistry } from "../../app/api/getMetadataRegistry";
import { IMetadataRegistryEntry } from "../../Orchestrator/Types/Interface/IMetadataRegistry";

/**
 * One session-wide read of GET /metadata/registry, shared by every key that
 * asks. The payload is cached rather than any one parsed value: the registry is
 * a flat table serving several unrelated settings (`transfert_shuttle`,
 * `transfert_car_rental`, ...), and caching per-key would fire one identical GET
 * per consumer for a payload that already arrived.
 */

/**
 * Every `transfert_*` key is a transfer gap of minutes, not hours. A value past
 * this is not a config change, it is a typo — 4 hours would empty the
 * Recommended section on every shuttle in the product, and strand a car rental
 * half a day off its train. Reject rather than clamp: silently honouring half of
 * a wrong value hides the mistake.
 *
 * It is the DEFAULT ceiling, not a universal one: this table also carries keys
 * that are not transfer gaps, and they pass their own `max`. Widening this
 * constant to suit one of them would quietly loosen the guard on the shuttle
 * keys, where it is load-bearing.
 */
const MAX_BUFFER_MINUTES = 240;

/**
 * Registry values are strings, the key may be absent, and the whole payload is
 * outside our control — so nothing here may assume its own type declaration.
 * The array may not be an array; `value` may arrive as a JSON number. Anything
 * we can't read as sane minutes falls back to the caller's default.
 *
 * Zero is rejected by DEFAULT, and that default belongs to the shuttle gap: it
 * parses cleanly and passes a `< 0` check, but it switches off the very rule
 * MIN_CONNECTION_MINUTES exists to enforce, and an unfilled numeric registry row
 * is exactly what serialises as 0.
 *
 * `allowZero` opts a key out of that, for a buffer where "no gap" is a setting
 * someone can legitimately choose rather than a row nobody filled in. The car
 * rental gap is one — the mobile app treats 0 as a real value there, and a key
 * ops can set but we silently overrule is worse than a key we take literally.
 */
export function parseRegistryMinutes(
    entries: IMetadataRegistryEntry[] | null | undefined,
    key: string,
    fallback: number,
    { allowZero = false, max = MAX_BUFFER_MINUTES }: { allowZero?: boolean; max?: number } = {}
): number {
    // Matched case-insensitively: the registry already carries an uppercase key
    // elsewhere, so casing there is not a promise.
    const entry = Array.isArray(entries)
        ? entries.find((item) => String(item?.key ?? "").toLowerCase() === key.toLowerCase())
        : undefined;

    // String() so a numeric `value` doesn't throw on .trim(); the empty result
    // is rejected below, which also covers `Number("") === 0`.
    const raw = String(entry?.value ?? "").trim();
    if (!raw) return fallback;

    // Round BEFORE the range check, not after: validating 0.4 as "> 0" and
    // then rounding it produces exactly the zero buffer rejected above. Under
    // allowZero that rounding is what turns 0.4 into an honest 0 rather than a
    // fractional minute nobody meant.
    const minutes = Math.round(Number(raw));
    const belowFloor = allowZero ? minutes < 0 : minutes <= 0;
    if (!Number.isFinite(minutes) || belowFloor || minutes > max) {
        console.warn(`[registry] ignoring unusable ${key} value "${raw}"`);
        return fallback;
    }

    return minutes;
}

/** Statuses that mean "this deployment has no such route", not "try again". */
const ENDPOINT_ABSENT_CODES = new Set([404, 405, 501]);

// One read per session, shared by every key that asks. `null` means "not read
// yet"; `[]` means "read, and there was nothing in it" — an empty payload is a
// settled answer, so the check below is `!== null`, never a truthiness or
// length test, or a served-but-unseeded registry would be re-fetched forever.
let cachedEntries: IMetadataRegistryEntry[] | null = null;
let inflight: Promise<IMetadataRegistryEntry[]> | null = null;

/** The settled payload, or null if nothing has been read yet. Lets a render or
 *  a click handler read a warm cache synchronously instead of flashing the
 *  fallback for one tick. */
export function peekRegistryEntries(): IMetadataRegistryEntry[] | null {
    return cachedEntries;
}

/**
 * [forceRefresh] skips the session cache and always goes to the network (the
 * From/To picker re-reads `stops_whitelist` on every open). A read already in
 * flight is reused — it is a fresh network answer either way. On failure the
 * last settled payload is kept, so a flaky network falls back to the last known
 * values instead of wiping them.
 */
export async function loadRegistryEntries(
    { forceRefresh = false }: { forceRefresh?: boolean } = {}
): Promise<IMetadataRegistryEntry[]> {
    if (!forceRefresh && cachedEntries !== null) return cachedEntries;

    if (!inflight) {
        const request: Promise<IMetadataRegistryEntry[]> = getMetadataRegistry()
            .then((response) => {
                if (response.status !== "ok") {
                    // Only "this environment doesn't serve the route" is cached
                    // — that answer can't change mid-session, and re-asking on
                    // every modal open costs a guaranteed 404 each time.
                    // Everything else retries, including the rest of the 4xx
                    // range: the warm read fires inside the TOKEN_READY burst
                    // alongside stops/ticket-options/places, so a 401 on a
                    // still-settling token or a 429 from that burst is exactly
                    // the transient we must not pin the fallback on.
                    if (ENDPOINT_ABSENT_CODES.has(response.code)) {
                        cachedEntries = [];
                    }
                    return cachedEntries ?? [];
                }
                cachedEntries = Array.isArray(response.answer) ? response.answer : [];
                return cachedEntries;
            })
            .catch(() => cachedEntries ?? [])
            .finally(() => {
                // Only clear our own slot: a reset mid-flight may already have
                // put a newer request here, and nulling that would let a
                // duplicate fetch through.
                if (inflight === request) inflight = null;
            });
        inflight = request;
    }

    return inflight;
}

/** Test seam: drops the session cache so each case starts from a clean read. */
export function resetRegistryCache(): void {
    cachedEntries = null;
    inflight = null;
}
